"use client"

import React, { useState, useContext, useEffect, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  Chip,
  Typography,
} from "@mui/material"
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

import dayjs from "dayjs"

import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

const TransaccionModal = ({ open, onClose, fechaTransaccion, onTransactionAdded }) => {
  const {
    getTipoTransaccion,
    tipoTransaccion,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    postTransaccion,
    putTransaccion,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    user,
    postCrearPago,
    getCuentaCorrienteByIdEntidad,
    updateCuentaCorriente,
    postCuentaCorriente,
    postTransaccionPago,
    getFechaHoraActualClient,
    getUbicacion,
    ubicaciones,
    getTipoEntidad,
    tipoEntidades,
    getSubCategoriaTransaccion,
    subCategoriaTransaccion,
    getCategoriaTransaccion,
    categoriaTransaccion,
    formatoNumericoGlobal,
    getParametrosGlobales,
  } = useContext(ErpContext)
  
  // Estado local para parámetros globales del modal
  const [parametrosGlobalesLocal, setParametrosGlobalesLocal] = useState({})

  const [formData, setFormData] = useState({
    tipoTransaccion: null,
    tipoMedioDePago: null,
    medioDePago: null,
    ubicacion: null,
    ubicacionDestino: null, // Para transferencias internas
    monto: "", // Valor numérico interno (number o "")
    descripcion: "",
    tipoEntidad: null,
    entidad: null,
    subcategoria: null,
  })
  const [montoDisplay, setMontoDisplay] = useState("") // Valor formateado para mostrar
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [dniCliente, setDniCliente] = useState(null);
  const [fechaTrans, setFechaTrans] = useState(fechaTransaccion);
  const [tiposEntidadFiltrados, setTiposEntidadFiltrados] = useState([])
  const [categoriaPadre, setCategoriaPadre] = useState("")
  const [ubicacionesDestino, setUbicacionesDestino] = useState([])

  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  useEffect(() => {
    if (open) {
      // Cargar datos necesarios cuando se abre el modal
      fetchInitialData()
      // Cargar parámetros globales para transferencia interna
      loadParametrosGlobales()
    }
  }, [open])
  
  // Cargar parámetros globales necesarios para el modal
  const loadParametrosGlobales = async () => {
    try {
      const resultado = await getParametrosGlobales(["usaTransferenciaInternaCaja"])
      setParametrosGlobalesLocal(resultado || {})
    } catch (error) {
      console.error("Error cargando parámetros globales:", error)
    }
  }

  useEffect(() => {
  if (fechaTransaccion) {
    setFechaTrans(dayjs(fechaTransaccion))
  }
}, [fechaTransaccion])

  useEffect(() => {
    if (formData.tipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(formData.tipoMedioDePago)
    }
  }, [formData.tipoMedioDePago])

  useEffect(() => {
    if (tipoEntidades.length > 0) {
      const filtrados = tipoEntidades.filter(tipo => tipo.verEnCaja === true)
      setTiposEntidadFiltrados(filtrados)
    }
  }, [tipoEntidades])

  // Filtrar ubicaciones destino para transferencias internas
  useEffect(() => {
    if (ubicaciones.length > 0) {
      const ubicacionesConDestino = ubicaciones.filter(ub => ub.destinoTransferenciaInternaCaja === true)
      setUbicacionesDestino(ubicacionesConDestino)
    }
  }, [ubicaciones])

  // Opciones del dropdown de Tipo de Transacción (con opción virtual "Transferencia Interna")
  const opcionesTipoTransaccion = useMemo(() => {
    // Filtrar tipos que tienen verEnCaja = true
    const tiposBase = (tipoTransaccion || []).filter(t => t.verEnCaja === true)
    
    // Si el parámetro global está activo (valor "1" string), agregar opción virtual
    if (parametrosGlobalesLocal?.usaTransferenciaInternaCaja === "1") {
      tiposBase.push({
        id: "transferencia-interna", // ID virtual (string para diferenciarlo)
        descripcion: "Transferencia Interna"
      })
    }
    
    return tiposBase
  }, [tipoTransaccion, parametrosGlobalesLocal])

  useEffect(() => {
    if (searchTextCliente.length >= 3 && formData.tipoEntidad) {
      getEntidadesClientesTresLetras(searchTextCliente, [formData.tipoEntidad.id], false, false)
    }
  }, [searchTextCliente, formData.tipoEntidad])

  useEffect(() => {
    if (formData.subcategoria) {
      const subcatObj = subCategoriaTransaccion.find(s => s.id === formData.subcategoria.id)
      if (subcatObj && subcatObj.idCategoriaTransaccion) {
        const catPadre = categoriaTransaccion.find(c => c.id === subcatObj.idCategoriaTransaccion)
        setCategoriaPadre(catPadre?.descripcion || "")
      }
    } else {
      setCategoriaPadre("")
    }
  }, [formData.subcategoria, subCategoriaTransaccion, categoriaTransaccion])

  useEffect(() => {
    if (formData.tipoEntidad) {
      setFormData((prev) => ({ ...prev, entidad: null }))
      setDniCliente(null)
      setSearchTextCliente("")
    }
  }, [formData.tipoEntidad])

  // Limpiar subcategoría cuando cambiamos de tipo de transacción y no es egreso
  useEffect(() => {
    if (formData.tipoTransaccion && formData.tipoTransaccion.id !== 5) {
      setFormData((prev) => ({ ...prev, subcategoria: null }))
      setCategoriaPadre("")
    }
  }, [formData.tipoTransaccion])

  // Detectar si es Transferencia Interna (basado en el ID virtual "transferencia-interna")
  const esTransferenciaInterna = formData.tipoTransaccion?.id === "transferencia-interna"

  // Manejar cambios cuando se selecciona Transferencia Interna
  useEffect(() => {
    const configurarTransferenciaInterna = async () => {
      if (esTransferenciaInterna) {
        // Forzar tipo de entidad a Empleado (esto sí se mantiene)
        const tipoEmpleado = tipoEntidades.find(t => t.descripcion === "Empleado")
        if (tipoEmpleado && formData.tipoEntidad?.id !== tipoEmpleado.id) {
          setFormData((prev) => ({ ...prev, tipoEntidad: tipoEmpleado }))
        }

        // ✅ CAMBIO: Ya no forzamos tipo de medio de pago ni medio específico
        // El usuario puede seleccionar libremente cualquier medio de pago
      } else {
        // Limpiar ubicación destino si cambiamos a otro tipo
        setFormData((prev) => ({ ...prev, ubicacionDestino: null }))
      }
    }
    
    configurarTransferenciaInterna()
  }, [esTransferenciaInterna, tipoEntidades])

  // Sincronizar montoDisplay con formData.monto cuando cambia desde fuera
  useEffect(() => {
    if (formData.monto === "" || formData.monto == null) {
      setMontoDisplay("")
      return
    }

    const locale = localeConfig.locale
    const decimalSeparator = localeConfig.decimalSeparator

    if (typeof formData.monto === "number") {
      let formatted = formatInputWithSeparators(formData.monto, locale)
      setMontoDisplay(formatted)
    } else if (typeof formData.monto === "string") {
      const parsed = parseLocalizedNumber(formData.monto, locale)
      if (parsed !== null) {
        let formatted = formatInputWithSeparators(parsed, locale)
        if (formData.monto.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
          formatted += decimalSeparator
        }
        setMontoDisplay(formatted)
      }
    }
  }, [formData.monto, localeConfig])

  const fetchInitialData = async () => {
    await getTipoTransaccion()
    await getTipoMedioDePago()
    await getUbicacion()
    await getTipoEntidad()
    await getSubCategoriaTransaccion()
    await getCategoriaTransaccion()
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError("")

    try {
      // Validar que el monto sea un número válido
      const montoValue = typeof formData.monto === "number" 
        ? formData.monto 
        : parseLocalizedNumber(formData.monto, localeConfig.locale)
      
      if (montoValue === null || montoValue <= 0) {
        setError("El monto debe ser un número mayor a 0")
        setLoading(false)
        return
      }
      
      const idEntidad = formData.entidad?.id || null
      
      // Construir fecha con la hora actual: tomar la fecha del DatePicker pero con la hora actual
      const fechaConHoraActual = dayjs(fechaTrans).hour(dayjs().hour()).minute(dayjs().minute()).second(dayjs().second())
      const {fechaServidor, zona} = getFechaHoraActualClient(fechaConHoraActual)

      // **CASO ESPECIAL: TRANSFERENCIA INTERNA**
      if (esTransferenciaInterna) {
        // 1. Crear EGRESO (tipo 7 - ubicación origen) - SIN transaccionAsociada
        const egresoResponse = await postTransaccion(
          montoValue,
          7, // Tipo transacción: Transferencia Interna ORIGEN (ID = 7)
          idEntidad,
          null, // sin subcategoría
          formData.descripcion,
          user.id,
          formData.ubicacion?.id || 1, // Ubicación ORIGEN
          1,
          0,
          0,
          null, // 🔑 Inicialmente sin transaccionAsociada
          fechaServidor,
        )

        const idTransaccionEgreso = egresoResponse.data.id

        // Crear pago para el egreso
        const pagoEgreso = await postCrearPago(
          1,
          1,
          formData.medioDePago?.id,
          montoValue
        )
        await postTransaccionPago(idTransaccionEgreso, pagoEgreso.data.id)

        // 2. Crear INGRESO (tipo 8 - ubicación destino) - CON transaccionAsociada apuntando al egreso
        const ingresoResponse = await postTransaccion(
          montoValue,
          8, // Tipo transacción: Transferencia Interna DESTINO (ID = 8)
          idEntidad,
          null, // sin subcategoría
          formData.descripcion,
          user.id,
          formData.ubicacionDestino?.id, // Ubicación DESTINO
          1,
          0,
          0,
          idTransaccionEgreso, // 🔑 Apunta al EGRESO
          fechaServidor,
        )

        const idTransaccionIngreso = ingresoResponse.data.id

        // Crear pago para el ingreso
        const pagoIngreso = await postCrearPago(
          1,
          1,
          formData.medioDePago?.id,
          montoValue
        )
        await postTransaccionPago(idTransaccionIngreso, pagoIngreso.data.id)

        // 3. Actualizar EGRESO para que apunte al INGRESO (vinculación bidireccional)
        await putTransaccion(
          idTransaccionEgreso,
          montoValue,
          7,
          idEntidad,
          null,
          formData.descripcion,
          user.id,
          formData.ubicacion?.id || 1,
          1,
          0,
          0,
          idTransaccionIngreso, // 🔑 Ahora el EGRESO apunta al INGRESO
          fechaServidor
        )

      } else {
        // **CASO NORMAL: Crear una sola transacción**
        const transactionData = {
          idTipoTransaccion: formData.tipoTransaccion?.id,
          idMedioDePago: formData.medioDePago?.id,
          monto: montoValue,
          descripcion: formData.descripcion,
        }
        
        const transaccionResponse = await postTransaccion(
          transactionData.monto,
          transactionData.idTipoTransaccion,
          idEntidad,
          formData.subcategoria?.id || null,
          transactionData.descripcion,
          user.id,
          formData.ubicacion?.id || 1,
          1,
          0,
          0,
          null, // transaccionAsociada
          fechaServidor, // fecha
        )

        const idTransaccion = transaccionResponse.data.id

        // Sector de pagos
        const transaccionCrearPago = await postCrearPago(
          1,
          1,
          transactionData.idMedioDePago,
          transactionData.monto
        )

        const idTransaccionCrearPago = transaccionCrearPago.data.id
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago)
      }

      // Resetear formulario
      setFormData({
        tipoTransaccion: null,
        tipoMedioDePago: null,
        medioDePago: null,
        ubicacion: null,
        ubicacionDestino: null,
        monto: "",
        descripcion: "",
        tipoEntidad: null,
        entidad: null,
        subcategoria: null,
      })
      setMontoDisplay("")
      setDniCliente(null)
      setSearchTextCliente("")
      setFechaTrans(dayjs())
      setCategoriaPadre("")

      // Cerrar modal y refrescar datos, pasar la ubicación de la transacción creada
      onTransactionAdded(fechaServidor, zona, formData.ubicacion?.id)
      onClose()
    } catch (err) {
      setError("Error al crear la transacción")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({
        tipoTransaccion: null,
        tipoMedioDePago: null,
        medioDePago: null,
        ubicacion: null,
        ubicacionDestino: null,
        monto: "",
        descripcion: "",
        tipoEntidad: null,
        entidad: null,
        subcategoria: null,
      })
      setMontoDisplay("")
      setDniCliente(null)
      setSearchTextCliente("")
      setFechaTrans(dayjs())
      setCategoriaPadre("")
      setError("")
      onClose()
    }
  }

  const esEgreso = formData.tipoTransaccion?.id === 5
  const isFormValid = 
    formData.tipoTransaccion && 
    formData.medioDePago && 
    formData.monto && 
    formData.ubicacion &&
    (esEgreso ? (formData.subcategoria && formData.entidad) : true) &&
    (esTransferenciaInterna ? (formData.ubicacionDestino && formData.entidad) : true)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#5b5b5f",
          color: "white",
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        Agregar Nueva Transacción
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {/* 1. Fecha */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Fecha"
              format="DD/MM/YYYY"
              disableFuture
              maxDate={dayjs()}
              size="small"
              value={fechaTrans}
              onChange={(newValue) => setFechaTrans(newValue)}
              slotProps={{
                textField: { size: "small", inputProps: { maxLength: 10 } },
              }}
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: "100%" }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>

          {/* 2. Tipo de Transacción */}
          <Autocomplete
            options={opcionesTipoTransaccion}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.tipoTransaccion}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, tipoTransaccion: newValue }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Transacción *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* SubCategoría - Solo mostrar si es Egreso */}
          {formData.tipoTransaccion?.id === 5 && (
            <>
              <Autocomplete
                options={subCategoriaTransaccion || []}
                getOptionLabel={(option) => option.descripcion || ""}
                value={formData.subcategoria}
                size="small"
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, subcategoria: newValue }))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="SubCategoría *"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              
              {/* Leyenda de Categoría Padre */}
              {categoriaPadre && (
                <Box sx={{ mt: -2, mb: 1 }}>
                  <Chip
                    size="small"
                    label={`Categoría: ${categoriaPadre}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                </Box>
              )}
            </>
          )}

          {/* 3. Ubicación / Ubicación Origen y Destino */}
          {esTransferenciaInterna ? (
            // Transferencia Interna: Mostrar Origen y Destino
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Ubicación Origen */}
              <Autocomplete
                options={ubicaciones || []}
                getOptionLabel={(option) => option.descripcion || ""}
                value={formData.ubicacion}
                size="small"
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, ubicacion: newValue }))
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ubicación Origen *"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Ubicación Destino */}
              <Autocomplete
                options={ubicacionesDestino || []}
                getOptionLabel={(option) => option.descripcion || ""}
                value={formData.ubicacionDestino}
                size="small"
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, ubicacionDestino: newValue }))
                }}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ubicación Destino *"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ color: "rgb(76, 175, 80)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          ) : (
            // Otros tipos de transacción: Solo Ubicación
            <Autocomplete
              options={ubicaciones || []}
              getOptionLabel={(option) => option.descripcion || ""}
              value={formData.ubicacion}
              size="small"
              onChange={(event, newValue) => {
                setFormData((prev) => ({ ...prev, ubicacion: newValue }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación *"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* 4. Tipo de Entidad y 5. Entidad en un solo row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Tipo de Entidad - 35% */}
            <Autocomplete
              options={tiposEntidadFiltrados}
              getOptionLabel={(option) => option.descripcion || ""}
              value={formData.tipoEntidad}
              size="small"
              onChange={(event, newValue) => {
                setFormData((prev) => ({ ...prev, tipoEntidad: newValue }))
              }}
              disabled={esTransferenciaInterna} // Deshabilitado en transferencias internas
              sx={{ flex: "0 0 35%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={formData.tipoTransaccion?.id === 5 || esTransferenciaInterna ? "Tipo de Entidad *" : "Tipo de Entidad"}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* Entidad - 65% */}
            <Autocomplete
              size="small"
              options={entidadesClientesTresLetras.map((it) => ({
                label: it.descripcion + " - " + it.apellido + " - " + it.dniCuitCuil,
                id: it.id,
              }))}
              value={formData.entidad}
              onChange={(event, newValue) => {
                setFormData((prev) => ({ ...prev, entidad: newValue }))
                setDniCliente(newValue)
              }}
              onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
              disabled={!formData.tipoEntidad}
              sx={{ flex: "0 0 65%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={formData.tipoTransaccion?.id === 5 || esTransferenciaInterna ? "Entidad *" : "Entidad"}
                  variant="outlined"
                  placeholder={formData.tipoEntidad ? "Buscar entidad..." : "Primero seleccione tipo"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                    },
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 200,
                },
              }}
            />
          </Box>

          {/* 6. Tipo de Medio de Pago */}
          <Autocomplete
            options={(tipoMedioDePago || []).filter(t => t.id === 1 || t.id === 2|| t.id === 3|| t.id === 4)}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.tipoMedioDePago}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                tipoMedioDePago: newValue,
                medioDePago: null, // Reset medio de pago específico
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Medio de Pago *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 7. Medio de Pago Específico */}
          <Autocomplete
            options={medioDePagoIdTipoEspecifico || []}
            getOptionLabel={(option) => option.descripcion || ""}
            value={formData.medioDePago}
            size="small"
            onChange={(event, newValue) => {
              setFormData((prev) => ({ ...prev, medioDePago: newValue }))
            }}
            disabled={!formData.tipoMedioDePago}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Medio de Pago Específico *"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PointOfSaleIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* 8. Monto */}
          <TextField
            label="Monto *"
            type="text"
            variant="outlined"
            size="small"
            value={montoDisplay}
            onChange={(e) => {
              const input = e.target
              const inputValue = input.value
              const cursorPosition = input.selectionStart
              const { locale, decimalSeparator, thousandSeparator } = localeConfig

              if (inputValue === "") {
                setMontoDisplay("")
                setFormData((prev) => ({ ...prev, monto: "" }))
                return
              }

              const parsedValue = parseLocalizedNumber(inputValue, locale)

              if (parsedValue === null && inputValue.trim() !== "") {
                return
              }

              let formattedValue = parsedValue !== null
                ? formatInputWithSeparators(parsedValue, locale)
                : ""

              const hasDecimalSeparator = inputValue.includes(decimalSeparator)
              const endsWithDecimal = inputValue.endsWith(decimalSeparator)
              const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

              if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                formattedValue += decimalSeparator
              } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                const integerPart = formattedValue.split(decimalSeparator)[0]
                formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
              }

              setMontoDisplay(formattedValue)
              setFormData((prev) => ({ ...prev, monto: parsedValue ?? "" }))

              // Calcular nueva posición del cursor
              setTimeout(() => {
                const beforeCursor = inputValue.substring(0, cursorPosition)
                // Contar solo caracteres numéricos y el separador decimal antes del cursor
                const numericCharsBefore = beforeCursor.replace(new RegExp(`\\${thousandSeparator}`, "g"), "").length
                
                let newCursorPosition = formattedValue.length
                
                // Si estamos escribiendo números, intentar mantener posición relativa
                if (numericCharsBefore > 0) {
                  let numericCount = 0
                  for (let i = 0; i < formattedValue.length; i++) {
                    const char = formattedValue[i]
                    if (char !== thousandSeparator) {
                      numericCount++
                      if (numericCount >= numericCharsBefore) {
                        newCursorPosition = i + 1
                        break
                      }
                    }
                  }
                }
                
                input.setSelectionRange(newCursorPosition, newCursorPosition)
              }, 0)
            }}
            onBlur={(e) => {
              const { locale } = localeConfig
              const parsed = parseLocalizedNumber(e.target.value, locale)
              if (parsed !== null && parsed > 0) {
                const formatted = formatInputWithSeparators(parsed, locale)
                setMontoDisplay(formatted)
                setFormData((prev) => ({ ...prev, monto: parsed }))
              } else if (e.target.value === "") {
                setMontoDisplay("")
                setFormData((prev) => ({ ...prev, monto: "" }))
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "decimal",
              autoComplete: "off",
            }}
          />

          {/* 9. Descripción */}
          <TextField
            label="Descripción (Opcional)"
            variant="outlined"
            multiline
            size="small"
            rows={3}
            value={formData.descripcion}
            onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
            placeholder="Ingrese una descripción para la transacción..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderColor: "#5b5b5f",
            color: "#5b5b5f",
            "&:hover": {
              borderColor: "rgb(244, 177, 14)",
              color: "rgb(244, 177, 14)",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
          sx={{
            backgroundColor: "#5b5b5f",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(244, 177, 14, 0.8)",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
              Guardando...
            </>
          ) : (
            "Agregar Transacción"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TransaccionModal
